/**
 * The date window to search in when somebody types a call id.
 *
 * <h3>Why a call-id search needs a window at all</h3>
 * It looks like it should not: an id identifies ONE call, so a date filter can
 * only hide it. But `callId` is not indexed on the tvoice backend, and the
 * window is the only thing keeping the query tractable. Measured 2026-09-08
 * against the live Africa API, same call id:
 *
 * <pre>
 *   no window at all      -> still running at 60s
 *   the UI's 2-day range  -> HTTP 504 after 61s
 *   ±60 min from the id   -> HTTP 200 in 3.0s
 *   ±30 min from the id   -> HTTP 200 in 1.6s
 *   ±5  min from the id   -> HTTP 200 in 1.2s
 * </pre>
 *
 * axios gives up at 30s, so both of the obvious answers fail: keeping the
 * default range is too slow, and clearing it is unbounded and slower still.
 *
 * <h3>The window comes from the id</h3>
 * An Asterisk unique id is `{epochSeconds}.{channelCounter}` — the call's own
 * start time. So the search can be pinned to the moment the call happened,
 * which is both FAST and correct for a call of any age. A call from six months
 * ago is found just as easily as one from this morning, which is exactly what
 * the default range could never do.
 */

/**
 * ±30 minutes.
 *
 * <p>The `+` side is not symmetry for its own sake. The id's epoch is the call's
 * START, but the row is filtered on `created_at`, which is written at call END —
 * a session observed 2026-09-08 was created 99 seconds after its id's epoch. So
 * the forward half has to cover the call's whole duration, and ±15 would miss
 * any call longer than fifteen minutes.
 *
 * <p>The `-` side only needs to absorb clock skew between the switch and the
 * database, so it is generous rather than tight.
 */
const WINDOW_SECONDS = 30 * 60;

export interface CallIdWindow {
  /** Epoch MILLISECONDS — what the API expects; `dateStringToEpoch` also returns ms. */
  fromEpochMs: number;
  toEpochMs: number;
}

/**
 * A window around the call the id names, or null when the id is not an Asterisk
 * unique id.
 *
 * <p>Null means "leave the existing range alone" — a partial id typed halfway
 * through, or some other identifier, must not silently move the user's dates.
 */
export function windowForCallId(callId: string | null | undefined): CallIdWindow | null {
  if (!callId) return null;
  const head = String(callId).trim().split('.')[0];
  // At least 9 digits: an epoch, not a channel number or a fragment.
  if (!/^\d{9,}$/.test(head)) return null;
  const epoch = Number(head);
  if (!Number.isFinite(epoch) || epoch <= 0) return null;
  return {
    fromEpochMs: (epoch - WINDOW_SECONDS) * 1000,
    toEpochMs: (epoch + WINDOW_SECONDS) * 1000,
  };
}

/** `YYYY-MM-DDTHH:mm` in local time — the shape the filter bars hold dates in. */
export function toLocalDateTimeInput(epochMs: number): string {
  const d = new Date(epochMs);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
